import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

// ════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════

interface Announcement {
  id: string;
  title: string;
  content: string;
  targetAudience: string;
  targetSessionId: string | null;
  priority: string;
  createdAt: number;
  createdByEmail: string | null;
}

interface AnnouncementsResponse {
  announcements: Announcement[];
  pagination: {
    limit: number;
    hasMore: boolean;
    nextCursor: string | null;
    total?: number;
  };
}

type TargetAudience = "all_parents" | "all_coaches" | "all_members" | "session_attendees";
type Priority = "low" | "normal" | "high" | "urgent";

// ════════════════════════════════════════════════════════════
// PARENT HOOKS
// ════════════════════════════════════════════════════════════

export function useParentAnnouncements(limit = 5) {
  const data = useQuery(api.parent.announcements.list, { limit });
  return { data, isLoading: data === undefined, error: null };
}

// ════════════════════════════════════════════════════════════
// COACH HOOKS
// ════════════════════════════════════════════════════════════

export function useCoachAnnouncements(limit = 5) {
  const data = useQuery(api.coach.announcements.list, { limit });
  return { data, isLoading: data === undefined, error: null };
}

// ════════════════════════════════════════════════════════════
// DIRECTOR HOOKS
// ════════════════════════════════════════════════════════════

export function useDirectorAnnouncements(limit = 20) {
  const data = useQuery(api.director.announcements.list, { limit });
  return { data, isLoading: data === undefined, error: null };
}

interface CreateAnnouncementData {
  title: string;
  content: string;
  targetAudience: string;
  targetSessionId?: string;
  priority?: string;
}

export function useCreateAnnouncement() {
  const mutate = useMutation(api.director.announcements.create);
  return {
    mutateAsync: (data: CreateAnnouncementData) => mutate({
      title: data.title,
      content: data.content,
      targetAudience: data.targetAudience as TargetAudience,
      targetSessionId: data.targetSessionId ? data.targetSessionId as Id<"trainingSessions"> : undefined,
      priority: data.priority as Priority | undefined,
    }),
    mutate: (data: CreateAnnouncementData) => { mutate({
      title: data.title,
      content: data.content,
      targetAudience: data.targetAudience as TargetAudience,
      targetSessionId: data.targetSessionId ? data.targetSessionId as Id<"trainingSessions"> : undefined,
      priority: data.priority as Priority | undefined,
    }); },
  };
}

export function useDeleteAnnouncement() {
  const mutate = useMutation(api.director.announcements.remove);
  return {
    mutateAsync: (announcementId: string) => mutate({ announcementId: announcementId as Id<"announcements"> }),
    mutate: (announcementId: string) => { mutate({ announcementId: announcementId as Id<"announcements"> }); },
  };
}

// Coach create announcement
export function useCoachCreateAnnouncement() {
  const mutate = useMutation(api.coach.announcements.create);
  return {
    mutateAsync: (data: CreateAnnouncementData) => mutate({
      title: data.title,
      content: data.content,
      targetSessionId: data.targetSessionId as Id<"trainingSessions">,
      priority: data.priority as Priority | undefined,
    }),
    mutate: (data: CreateAnnouncementData) => { mutate({
      title: data.title,
      content: data.content,
      targetSessionId: data.targetSessionId as Id<"trainingSessions">,
      priority: data.priority as Priority | undefined,
    }); },
  };
}
